
import React, { useEffect, useMemo, useState } from 'react';
import './VendingMachine.css';

// ============================================
// FLASK API
// ============================================

const API_URL = 'vending_products';

// ============================================
// IMAGE URL HELPER
// ============================================

const productImageUrl = (image) => {
  if (!image) return '';

  if (/^https?:\/\//i.test(image)) return image;

  return `${process.env.PUBLIC_URL || ''}${image.startsWith('/') ? '' : '/'}${image}`;
};

// ============================================
// GET MULTIPLE PRODUCT IMAGES
// ============================================

const getProductImages = (product) => {
  let images = product.images || product.image_urls || [];

  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      images = images.split(',').map(i => i.trim()).filter(Boolean);
    }
  }

  if (!Array.isArray(images)) images = [];

  const result = images
    .map(item =>
      typeof item === 'string'
        ? item
        : item?.image_url || item?.url || item?.image
    )
    .filter(Boolean);

  if (product.image && !result.includes(product.image)) {
    result.unshift(product.image);
  }

  return [...new Set(result)];
};

// ============================================
// CURRENCY FORMATTER
// ============================================

const money = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);


// ============================================
// PRODUCT IMAGE COMPONENT
// ============================================

function ProductImage({ product, className = '' }) {
  const [failed, setFailed] = useState(!product.image);

  useEffect(() => {
    setFailed(!product.image);
  }, [product.image]);

  return (
    <div className={`vm-image-wrap ${className}`}>

      {!failed ? (
        <img
          src={productImageUrl(product.image)}
          alt={product.name}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="vm-image-placeholder">
          <span>IMAGE UNAVAILABLE</span>
          <small>{product.name}</small>
        </div>
      )}

    </div>
  );
}


// ============================================
// MAIN VENDING MACHINE COMPONENT
// ============================================

export default function VendingMachine() {

  // ------------------------------------------
  // PRODUCTS
  // ------------------------------------------

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ------------------------------------------
  // SEARCH AND CATEGORY
  // ------------------------------------------

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Products');

  // ------------------------------------------
  // SELECTED PRODUCT DETAILS
  // ------------------------------------------

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailQuantity, setDetailQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // ------------------------------------------
  // SHOPPING BASKET
  // ------------------------------------------

  const [cart, setCart] = useState([]);

  // ------------------------------------------
  // PAYMENT
  // ------------------------------------------

  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('UPI');
  const [demoStatus, setDemoStatus] = useState('');

const [previewProduct, setPreviewProduct] = useState(null);
  // ==========================================
  // FETCH PRODUCTS FROM FLASK
  // ==========================================

  useEffect(() => {

    let isMounted = true;

    const fetchProducts = async () => {

      try {

        setLoading(true);
        setLoadError('');

        const response = await fetch(API_URL);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success || !Array.isArray(data.products)) {
          throw new Error(data.message || 'Invalid API response');
        }

        const formattedProducts = data.products.map(product => {

          const image = product.image_url || '';

          return {
            id: product.product_id,
            name: product.product_name,
            category: product.category || 'Other',
            price: Number(product.price) || 0,
            description: product.description || '',
            pocketId: product.pocket_id || '',
            image,

            images: getProductImages({
              image,
              images: product.images,
              image_urls: product.image_urls,
            }),
          };

        });

        if (isMounted) {
          setProducts(formattedProducts);
        }

      } catch (error) {

        console.error('Error fetching products:', error);

        if (isMounted) {
          setLoadError(
            'Unable to load products. Please try again later.'
          );
        }

      } finally {

        if (isMounted) {
          setLoading(false);
        }

      }

    };

    fetchProducts();

    return () => {
      isMounted = false;
    };

  }, []);


  // ==========================================
  // DYNAMIC PRODUCT CATEGORIES
  // ==========================================

  const categories = useMemo(() => {

    return [
      'All Products',
      ...new Set(
        products.map(product => product.category).filter(Boolean)
      ),
    ];

  }, [products]);


  // ==========================================
  // FILTER PRODUCTS
  // ==========================================

  const filteredProducts = useMemo(() => {

    const term = search.trim().toLowerCase();

    return products.filter(product => {

      const matchesCategory =
        category === 'All Products' ||
        product.category === category;

      const matchesSearch =
        `${product.name} ${product.category} ${product.description} ${product.pocketId}`
          .toLowerCase()
          .includes(term);

      return matchesCategory && matchesSearch;

    });

  }, [products, search, category]);


  // ==========================================
  // OPEN PRODUCT DETAILS
  // ==========================================

  const openProductDetails = (product) => {

    setSelectedProduct(product);
    setDetailQuantity(1);
    setActiveImageIndex(0);
    setDemoStatus('');

  };


  // ==========================================
  // CLOSE PRODUCT DETAILS
  // ==========================================

  const closeProductDetails = () => {

    setSelectedProduct(null);
    setDetailQuantity(1);
    setActiveImageIndex(0);

  };


  // ==========================================
  // SELECTED PRODUCT IMAGES
  // ==========================================

  const selectedImages = selectedProduct
    ? getProductImages(selectedProduct)
    : [];


  // ==========================================
  // PREVIOUS IMAGE
  // ==========================================

  const previousImage = () => {

    if (selectedImages.length < 2) return;

    setActiveImageIndex(current =>
      (current - 1 + selectedImages.length) % selectedImages.length
    );

  };


  // ==========================================
  // NEXT IMAGE
  // ==========================================

  const nextImage = () => {

    if (selectedImages.length < 2) return;

    setActiveImageIndex(current =>
      (current + 1) % selectedImages.length
    );

  };


  // ==========================================
  // ADD PRODUCT TO BASKET
  // ==========================================

 const addToCart = (product) => {

  setDemoStatus('');

  setCart(current => {

    const existing = current.find(
      item => item.id === product.id
    );

    // Do not increase quantity if already added
    if (existing) {
      return current;
    }

    return [
      ...current,
      {
        ...product,
        quantity: 1,
      },
    ];

  });

};
  // ==========================================
  // ADD SELECTED PRODUCT AND OPEN PAYMENT
  // ==========================================

const addSelectedProductToCart = () => {
  if (!selectedProduct) return;

  addToCart(selectedProduct);

  closeProductDetails();

  setPaymentMethod('UPI');
  setDemoStatus('');
  setShowCheckout(true);
};

  // ==========================================
  // CHANGE BASKET QUANTITY
  // ==========================================

  const changeQuantity = (id, delta) => {

    setDemoStatus('');

    setCart(current =>

      current
        .map(item =>
          item.id === id
            ? {
                ...item,
                quantity: item.quantity + delta,
              }
            : item
        )
        .filter(item => item.quantity > 0)

    );

  };


  // ==========================================
  // BASKET ITEM COUNT
  // ==========================================

  const itemCount = cart.reduce(
    (sum, item) => sum + item.quantity,
    0
  );


  // ==========================================
  // BASKET TOTAL
  // ==========================================

  const total = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );


  // ==========================================
  // DEMO PAYMENT
  // ==========================================

  const startDemoPayment = () => {

    if (!cart.length) return;

    setDemoStatus(
      `Demo checkout ready for ${money(total)} using ${paymentMethod}. No money has been charged.`
    );

  };


  // ==========================================
  // RESET ORDER
  // ==========================================

  const resetOrder = () => {

    setCart([]);
    setShowCheckout(false);
    setDemoStatus('');
    setSearch('');
    setCategory('All Products');

    closeProductDetails();

  };


  // ==========================================
  // RENDER
  // ==========================================

  return (

    <div className="vm-app">


      {/* ======================================
          HEADER
      ====================================== */}

      <header className="vm-header">

        <a
          className="vm-brand"
          href="/vending-machine"
          aria-label="Vending Machine home"
        >

          <div className="vm-brand-mark">V</div>

          <div>

            <div className="vm-brand-name">
              Vending Machine
            </div>

            <div className="vm-brand-subtitle">
              CRAFTED WITH CARE � CHOSEN BY YOU
            </div>

          </div>

        </a>


        <div className="vm-header-note">

          <span className="vm-live-dot" />

          SELF-SERVICE KIOSK

        </div>

      </header>


      {/* ======================================
          HERO SECTION
      ====================================== */}

      <section className="vm-hero">

        <div className="vm-hero-copy">

          <span className="vm-eyebrow">
            A LITTLE SOMETHING HANDMADE
          </span>

          <h1>
            Discover something
            <br />
            <em>beautiful.</em>
          </h1>

          <p>
            Explore thoughtfully crafted keepsakes, home decor
            and gifts from local artisans.
          </p>


          <div className="vm-hero-pills">

            <span>Artisan picks</span>
            <span>Made with care</span>

          </div>


          <button
            type="button"
            className="vm-hero-button"
            onClick={() =>
              document.getElementById('vm-collection')?.scrollIntoView({
                behavior: 'smooth',
              })
            }
          >
            Explore collection
          </button>

        </div>


        <div className="vm-hero-art">

          <div className="vm-hero-circle" />


          {products[1] && (

            <div className="vm-hero-product vm-hero-product-one">

              <ProductImage product={products[1]} />

            </div>

          )}


          {products[0] && (

            <div className="vm-hero-product vm-hero-product-two">

              <ProductImage product={products[0]} />

            </div>

          )}


          <span className="vm-hero-star vm-hero-star-one">
            
          </span>

          <span className="vm-hero-star vm-hero-star-two">
            
          </span>

        </div>

      </section>


      {/* ======================================
          MAIN COLLECTION
      ====================================== */}

      <main className="vm-main" id="vm-collection">


        {/* SECTION HEADING */}

        <div className="vm-section-heading">

          <div>

            <span className="vm-eyebrow">
              THE COLLECTION
            </span>

            <h2>
              Choose your product
            </h2>

            <p>
              Tap on any product image to view its details.
            </p>

          </div>


          <label className="vm-search">

            <span aria-hidden="true">?</span>

            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search products..."
              aria-label="Search products"
            />


            {search && (

              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                x
              </button>

            )}

          </label>

        </div>


        {/* ==================================
            CATEGORY FILTER
        ================================== */}

        <div
          className="vm-category-row"
          aria-label="Product categories"
        >

          {categories.map(item => (

            <button
              type="button"
              key={item}
              className={`vm-category ${category === item ? 'active' : ''}`}
              onClick={() => {
                setCategory(item);
                closeProductDetails();
              }}
            >

              {item}

            </button>

          ))}

        </div>


        {/* ==================================
            TWO COLUMN CONTENT LAYOUT
        ================================== */}

       <div className={`vm-content-layout ${
  selectedProduct ? 'vm-product-open' : ''
}`}>


          {/* ==================================
              LEFT SIDE - PRODUCT IMAGES ONLY
          ================================== */}

          <section
            className="vm-products-section"
            aria-label="Products"
          >

            <div className="vm-results-line">

              <span>
                {loading
                  ? 'Loading products...'
                  : `${filteredProducts.length} products`}
              </span>

              <span>
                Tap an image to view details
              </span>

            </div>


            <div className="vm-products-grid">


              {/* LOADING */}

              {loading && (

                <div className="vm-empty">

                  <h3>Loading products...</h3>

                  <p>
                    Please wait while we load the collection.
                  </p>

                </div>

              )}


              {/* ERROR */}

              {!loading && loadError && (

                <div className="vm-empty">

                  <h3>Products unavailable</h3>

                  <p>{loadError}</p>

                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                  >
                    Try again
                  </button>

                </div>

              )}


              {/* PRODUCT IMAGES */}

              {!loading && !loadError &&

                filteredProducts.map(product => (

                  <article
                    className="vm-product-card"
                    key={product.id}
                  >

                    <button
                      type="button"
                      className={`vm-product-image-button ${
                        selectedProduct?.id === product.id
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => openProductDetails(product)}
                      aria-label={`View details for ${product.name}`}
                    >

                      <ProductImage
                        product={product}
                        className="vm-card-image"
                      />

                    </button>

                  </article>

                ))

              }


              {/* NO PRODUCTS */}

              {!loading &&
                !loadError &&
                filteredProducts.length === 0 && (

                  <div className="vm-empty">

                    <h3>No products found</h3>

                    <p>
                      Try another product name or category.
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setCategory('All Products');
                      }}
                    >
                      Show all products
                    </button>

                  </div>

                )}

            </div>

          </section>


          {/* ==================================
              RIGHT SIDE - BASKET / DETAILS
          ================================== */}

          <aside
            className={`vm-cart-panel ${
              selectedProduct ? 'vm-selected-panel' : ''
            }`}
          >


            {/* ==================================
                SELECTED PRODUCT DETAILS
            ================================== */}

            {selectedProduct ? (

              <div className="vm-selected-content">


                {/* BACK BUTTON */}

                <div className="vm-selected-header">

                  <button
                    type="button"
                    className="vm-back-button"
                    onClick={closeProductDetails}
                  >

                    Back to Products

                  </button>


                  <span className="vm-detail-category">
                    {selectedProduct.category}
                  </span>

                </div>


                {/* PRODUCT IMAGE + INFORMATION */}

                <div className="vm-selected-product">

  {/* LARGE PRODUCT IMAGE */}

  <div className="vm-selected-image-area">

    <ProductImage
      key={`${selectedProduct.id}-${activeImageIndex}`}
      product={{
        ...selectedProduct,
        image:
          selectedImages[activeImageIndex] ||
          selectedProduct.image,
      }}
      className="vm-selected-main-image"
    />

    {selectedImages.length > 1 && (
      <>
        <button
          type="button"
          className="vm-main-image-arrow vm-prev"
          onClick={previousImage}
          aria-label="Previous image"
        >
          �
        </button>

        <button
          type="button"
          className="vm-main-image-arrow vm-next"
          onClick={nextImage}
          aria-label="Next image"
        >
          �
        </button>
      </>
    )}

  </div>


  {/* PRODUCT INFORMATION */}

  <div className="vm-selected-information">

    <span className="vm-detail-category">
      {selectedProduct.category}
    </span>

    <h2>{selectedProduct.name}</h2>

    <strong className="vm-detail-price">
      {money(selectedProduct.price)}
    </strong>

    <p>
      {selectedProduct.description ||
        'A beautiful handcrafted product made with care.'}
    </p>

    <span className="vm-selected-pocket">
      Pocket ID: {selectedProduct.pocketId || 'Not assigned'}
    </span>

  </div>

</div>

                {/* IMAGE THUMBNAILS */}

                {selectedImages.length > 1 && (

                  <div className="vm-detail-thumbnails">


                    <button
                      type="button"
                      className="vm-image-arrow"
                      onClick={previousImage}
                      aria-label="Previous image"
                    >
                     
                    </button>


                    {selectedImages.map((image, index) => (

                      <button
                        type="button"
                        key={`${image}-${index}`}
                        className={`vm-detail-thumbnail ${
                          activeImageIndex === index ? 'active' : ''
                        }`}
                        onClick={() => setActiveImageIndex(index)}
                        aria-label={`View image ${index + 1}`}
                      >

                        <ProductImage
                          product={{
                            ...selectedProduct,
                            image,
                          }}
                        />

                      </button>

                    ))}


                    <button
                      type="button"
                      className="vm-image-arrow"
                      onClick={nextImage}
                      aria-label="Next image"
                    >
                      
                    </button>

                  </div>

                )}


                {/* QUANTITY */}

               




                {/* TOTAL AMOUNT */}

               {/* TOTAL AMOUNT */}

<div className="vm-detail-total">

  <strong>Total Amount</strong>

  <strong>
    {money(selectedProduct.price)}
  </strong>

</div>


{/* CONTINUE WITH UPI */}

<button
  type="button"
  className="vm-detail-buy"
  onClick={addSelectedProductToCart}
>
  Continue with UPI 
</button>

                {/* ADD TO BASKET */}

               

                {/* CONTINUE TO PAYMENT */}

             
              </div>


            ) : (


           /* ==================================
                  NORMAL SHOPPING BASKET
              ================================== */

              <>


                {/* BASKET HEADER */}

                <div className="vm-cart-title">

                  <div>

                    <span className="vm-eyebrow">
                      YOUR SELECTION
                    </span>

                    <h2>My basket</h2>

                  </div>


                  <span className="vm-cart-count">
                    {itemCount}
                  </span>

                </div>


                {/* EMPTY BASKET */}

                {cart.length === 0 ? (

                  <div className="vm-cart-empty">

                    <div className="vm-empty-basket">
                      
                    </div>

                    <strong>
                      Your basket is waiting
                    </strong>

                    <p>
                      Tap any product image to view its details
                      and add it to your basket.
                    </p>

                  </div>


                ) : (


                  /* BASKET ITEMS */

                  <div className="vm-cart-items">

                    {cart.map(item => (

                      <div
                        className="vm-cart-item"
                        key={item.id}
                      >

                        <button
  type="button"
  className="vm-cart-image-preview"
  onClick={() => setPreviewProduct(item)}
  aria-label={`Preview ${item.name}`}
>
  <ProductImage
    product={item}
    className="vm-cart-product-image"
  />
</button>

                        <div className="vm-cart-item-main">

                          <strong>
                            {item.name}
                          </strong>

                          <span>
                            {money(item.price)} each
                          </span>

                          <small>
                            Pocket ID: {item.pocketId || 'Not assigned'}
                          </small>


                          {/* QUANTITY CONTROLS */}

                          <div className="vm-qty-controls">

                            <button
                              type="button"
                              onClick={() =>
                                changeQuantity(item.id, -1)
                              }
                              aria-label={`Remove one ${item.name}`}
                            >
                              -
                            </button>


                            <span>
                              {item.quantity}
                            </span>


                           

                          </div>

                        </div>


                        <strong className="vm-line-total">

                          {money(item.price * item.quantity)}

                        </strong>

                      </div>

                    ))}

                  </div>

                )}


                        

                {/* BASKET SUMMARY */}

                <div className="vm-cart-summary">

                  <div className="vm-summary-line">

                    <span>Items</span>

                    <span>{itemCount}</span>

                  </div>


                  <div className="vm-summary-line">

                    <span>Delivery</span>

                    <span>At kiosk</span>

                  </div>


                  <div className="vm-total-row">

                    <strong>Total Amount</strong>

                    <strong>{money(total)}</strong>

                  </div>


                  <button
                    type="button"
                    className="vm-checkout-button"
                    disabled={!cart.length}
                    onClick={() => {
                      setShowCheckout(true);
                      setDemoStatus('');
                    }}
                  >

                    Continue with UPI

                  </button>


                  <p className="vm-secure-note">
                    Demo payment mode
                  </p>

                </div>


              </>

            )}

          </aside>


        </div>

      </main>


      {/* ======================================
          FOOTER
      ====================================== */}

      <footer className="vm-footer">

        <span>VENDING MACHINE</span>

        <span>
          Celebrating the art of handmade.
        </span>

        <span>DEMO STOREFRONT</span>

      </footer>


      {/* ======================================
          CHECKOUT MODAL
      ====================================== */}

     {/* ======================================
    UPI CHECKOUT MODAL
====================================== */}

{showCheckout && (

  <div
    className="vm-modal-backdrop"
    onMouseDown={event => {
      if (event.target === event.currentTarget) {
        setShowCheckout(false);
      }
    }}
  >

    <section
      className="vm-checkout-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vm-checkout-title"
    >

      <button
        type="button"
        className="vm-modal-close"
        onClick={() => setShowCheckout(false)}
        aria-label="Close checkout"
      >
        x
      </button>

      <span className="vm-eyebrow">
        SECURE CHECKOUT
      </span>

      <h2 id="vm-checkout-title">
        UPI Payment
      </h2>

      <p className="vm-modal-intro">
        Review your selected products and total amount.
      </p>

      {/* ORDER TOTAL */}

      <div className="vm-modal-total">

        <span>Total Amount</span>

        <strong>{money(total)}</strong>

      </div>

      {/* UPI PAYMENT OPTION */}

      <div className="vm-payment-options">

        <div className="vm-payment-option selected">

          <span className="vm-payment-symbol">
            UPI
          </span>

          <span>
            <strong>UPI Payment</strong>

            <small>
              Google Pay, PhonePe, BHIM
            </small>
          </span>

          <span></span>

        </div>

      </div>

      <p className="vm-demo-warning">
        Demo only. No real payment is initiated or collected.
      </p>

      {/* DEMO CONFIRMATION */}

      <button
        type="button"
        className="vm-checkout-button vm-pay-button"
        onClick={startDemoPayment}
      >
        Confirm Demo Order
      </button>

      {demoStatus && (

        <div className="vm-demo-status" role="status">

          <strong>Demo order prepared!</strong>

          <p>{demoStatus}</p>

          <button
            type="button"
            onClick={resetOrder}
          >
            Finish Order
          </button>

        </div>

      )}

    </section>

  </div>

)}
{/* ======================================
    PRODUCT IMAGE PREVIEW MODAL
====================================== */}

{previewProduct && (

  <div
    className="vm-preview-backdrop"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) {
        setPreviewProduct(null);
      }
    }}
  >

    <div className="vm-preview-modal">

      <button
        type="button"
        className="vm-preview-close"
        onClick={() => setPreviewProduct(null)}
        aria-label="Close image preview"
      >
        �
      </button>

      <div className="vm-preview-heading">
        <strong>{previewProduct.name}</strong>
        <span>{money(previewProduct.price)}</span>
      </div>

      <div className="vm-preview-image-container">

       <img
  src={productImageUrl(previewProduct.image)}
  alt={previewProduct.name}
  onClick={(event) => {
    event.currentTarget.classList.toggle('zoomed');
  }}
  onError={(event) => {
    event.currentTarget.style.display = 'none';
  }}
/>
      </div>

      <p className="vm-preview-hint">
        Click the image to zoom in or out
      </p>

    </div>

  </div>

)}
    </div>

  );

}
